import React, { useState, useEffect, useRef, useCallback } from "react";

const PLAY_STORE_URL = "/download";

const simModes = {
  abc: {
    icon: "🅰️",
    title: "A is for Apple",
    desc: "Phonics & interactive tracing mode",
    color: "from-pink-500 to-purple-600"
  },
  "123": {
    icon: "🔢",
    title: "1, 2, 3 Counting",
    desc: "Fun animal counting & math puzzles",
    color: "from-teal-500 to-emerald-600"
  },
  words: {
    icon: "✏️",
    title: "Word Completion",
    desc: "Missing letter vocabulary builder",
    color: "from-amber-500 to-orange-600"
  }
};

const langData = {
  english: {
    word: "Apple - A for Apple",
    icon: "🍎"
  },
  hindi: {
    word: "अ से अनार (Pomegranate)",
    icon: "🎰"
  },
  gujarati: {
    word: "અ થી અનાનસ (Pineapple)",
    icon: "🍍"
  }
};

const Home = () => {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [simMode, setSimMode] = useState("abc");
  const [selectedLang, setSelectedLang] = useState("english");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [gameScore, setGameScore] = useState(0);
  const [statDownloads, setStatDownloads] = useState("0K+");

  const audioCtxRef = useRef(null);
  const gameAreaRef = useRef(null);
  const bgCanvasRef = useRef(null);

  // Audio Synthesizer Logic
  const playInteractiveSound = useCallback((type) => {
    if (!audioEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          audioCtxRef.current = new AudioContext();
        }
      }
      const audioCtx = audioCtxRef.current;
      if (!audioCtx) return;
      if (audioCtx.state === "suspended") {
        audioCtx.resume();
      }

      const now = audioCtx.currentTime;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);

      if (type === "pop") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + 0.1);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
      } else if (type === "star") {
        osc.type = "triangle";
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.setValueAtTime(659.25, now + 0.1);
        osc.frequency.setValueAtTime(783.99, now + 0.2);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
        osc.start(now);
        osc.stop(now + 0.35);
      } else if (type === "click") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(300, now);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
      }
    } catch (e) {
      console.error("Audio error:", e);
    }
  }, [audioEnabled]);

  const toggleAudio = () => {
    const nextState = !audioEnabled;
    setAudioEnabled(nextState);
    if (nextState) {
      playInteractiveSound("click");
    }
  };

  const handleSimModeChange = (key) => {
    playInteractiveSound("click");
    setSimMode(key);
  };

  const handleLangChange = (lang) => {
    playInteractiveSound("click");
    setSelectedLang(lang);
  };

  const triggerConfetti = () => {
    if (typeof window.confetti === "function") {
      window.confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  };

  const openDownloadModal = () => {
    playInteractiveSound("star");
    setDownloadModalOpen(true);
    triggerConfetti();
  };

  const closeDownloadModal = () => {
    playInteractiveSound("click");
    setDownloadModalOpen(false);
  };

  const handleIosClick = (e) => {
    if (e) e.preventDefault();
    playInteractiveSound("pop");
    setIosModalOpen(true);
  };

  const resetMiniGame = () => {
    playInteractiveSound("click");
    setGameScore(0);
    if (gameAreaRef.current) {
      const bubbles = gameAreaRef.current.querySelectorAll(".game-bubble");
      bubbles.forEach((b) => b.remove());
    }
  };

  // Stats counter animation
  useEffect(() => {
    let count = 0;
    const target = 500;
    const interval = setInterval(() => {
      count += 10;
      if (count >= target) {
        setStatDownloads("500K+");
        clearInterval(interval);
      } else {
        setStatDownloads(`${count}K+`);
      }
    }, 30);
    return () => clearInterval(interval);
  }, []);

  // Background Canvas Animation
  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    const particles = [];
    const symbols = ["A", "B", "C", "1", "2", "★", "☁️", "🎈"];
    const colors = ["#EC4899", "#7C3AED", "#14B8A6", "#F59E0B", "#3B82F6"];

    for (let i = 0; i < 25; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 16 + 12,
        symbol: symbols[Math.floor(Math.random() * symbols.length)],
        color: colors[Math.floor(Math.random() * colors.length)],
        speedY: Math.random() * 0.5 + 0.2,
        speedX: (Math.random() - 0.5) * 0.3,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.02
      });
    }

    let animFrameId;
    const animateBg = () => {
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.y -= p.speedY;
        p.x += p.speedX;
        p.angle += p.spin;

        if (p.y < -30) {
          p.y = height + 30;
          p.x = Math.random() * width;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.font = `bold ${p.size}px Outfit, sans-serif`;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.5;
        ctx.fillText(p.symbol, 0, 0);
        ctx.restore();
      });

      animFrameId = requestAnimationFrame(animateBg);
    };

    animateBg();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animFrameId);
    };
  }, []);

  // Mini-Game Bubbles Spawner
  useEffect(() => {
    const bubbleChars = ["A", "B", "C", "1", "2", "3", "★", "🍎", "🐱", "⭐"];
    const bubbleColors = [
      "bg-pink-500",
      "bg-purple-500",
      "bg-amber-400",
      "bg-teal-400",
      "bg-blue-500",
      "bg-rose-500"
    ];

    const createBubble = () => {
      const gameArea = gameAreaRef.current;
      if (!gameArea) return;

      const bubble = document.createElement("div");
      const char = bubbleChars[Math.floor(Math.random() * bubbleChars.length)];
      const color = bubbleColors[Math.floor(Math.random() * bubbleColors.length)];
      const size = Math.floor(Math.random() * 20) + 50;

      const posX = Math.random() * (gameArea.clientWidth - size);
      let posY = gameArea.clientHeight + 20;

      bubble.className = `game-bubble absolute rounded-full ${color} text-white font-bold heading-font flex items-center justify-center shadow-lg border-2 border-white/40 cursor-pointer select-none transition-transform hover:scale-110 z-10`;
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;
      bubble.style.left = `${posX}px`;
      bubble.style.top = `${posY}px`;
      bubble.style.fontSize = `${size * 0.4}px`;
      bubble.textContent = char;

      const speed = Math.random() * 1.5 + 1;
      const interval = setInterval(() => {
        posY -= speed;
        bubble.style.top = `${posY}px`;

        if (posY < -80) {
          clearInterval(interval);
          if (bubble.parentElement) {
            bubble.remove();
          }
        }
      }, 20);

      const popBubble = (e) => {
        e.stopPropagation();
        playInteractiveSound("pop");
        setGameScore((prev) => {
          const newScore = prev + 1;
          if (newScore % 5 === 0) {
            playInteractiveSound("star");
          }
          return newScore;
        });

        bubble.style.transform = "scale(1.5)";
        bubble.style.opacity = "0";
        clearInterval(interval);
        setTimeout(() => {
          if (bubble.parentElement) bubble.remove();
        }, 150);
      };

      bubble.addEventListener("mousedown", popBubble);
      bubble.addEventListener("touchstart", popBubble, { passive: true });

      gameArea.appendChild(bubble);
    };

    const spawner = setInterval(createBubble, 1200);
    return () => clearInterval(spawner);
  }, [playInteractiveSound]);

  const currentSim = simModes[simMode];
  const currentLang = langData[selectedLang];

  return (
    <div className="bg-mesh text-slate-800 antialiased min-h-screen flex flex-col justify-between relative" id="top">
      {/* Background Animated Canvas */}
      <canvas
        ref={bgCanvasRef}
        className="fixed inset-0 pointer-events-none z-0 opacity-40"
      />

      {/* Header */}
      <header className="sticky top-0 z-50 px-2 sm:px-4 lg:px-12 py-2 sm:py-3 transition-all duration-300">
        <div className="max-w-7xl mx-auto glass-card rounded-2xl px-3 sm:px-6 py-2.5 sm:py-3 shadow-lg flex items-center justify-between">
          {/* Brand Logo */}
          <a href="#top" className="flex items-center gap-2 sm:gap-3 group">
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl overflow-hidden shadow-md group-hover:scale-105 border-2 border-purple-200 transition-transform duration-300 bg-white flex items-center justify-center p-0.5">
              <img
                src="/image/logo.jpg"
                alt="KidsGuru Logo"
                className="w-full h-full object-cover rounded-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/image/logo.webp";
                }}
              />
            </div>
            <div className="flex flex-col">
              <span className="heading-font text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent leading-none">
                KidsGuru
              </span>
              <span className="text-[9px] sm:text-[10px] font-semibold tracking-wider text-purple-600 uppercase">
                Interactive Learning
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-700">
            <a
              href="#features"
              className="hover:text-pink-500 transition-colors flex items-center gap-1.5"
            >
              <i className="fa-solid fa-star text-amber-400 text-xs"></i> Features
            </a>
            <a
              href="#demo"
              className="hover:text-pink-500 transition-colors flex items-center gap-1.5"
            >
              <i className="fa-solid fa-gamepad text-purple-500 text-xs"></i> Mini Game
            </a>
            <a
              href="#preview"
              className="hover:text-pink-500 transition-colors flex items-center gap-1.5"
            >
              <i className="fa-solid fa-mobile-screen-button text-teal-500 text-xs"></i> App Preview
            </a>
            <a
              href="#languages"
              className="hover:text-pink-500 transition-colors flex items-center gap-1.5"
            >
              <i className="fa-solid fa-language text-blue-500 text-xs"></i> Languages
            </a>
          </nav>

          {/* Sound Toggle & CTA Button */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleAudio}
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${audioEnabled ? "bg-purple-100 text-purple-700" : "bg-slate-200 text-slate-500"
                } flex items-center justify-center transition-all`}
              title="Toggle Sound Effects"
            >
              <i
                className={`fa-solid ${audioEnabled ? "fa-volume-high" : "fa-volume-xmark"
                  } text-xs sm:text-sm`}
              ></i>
            </button>

            <button
              onClick={openDownloadModal}
              className="px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 sm:gap-2"
            >
              <i className="fa-solid fa-cloud-arrow-down text-xs sm:text-sm"></i>
              <span>Get App</span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => {
                playInteractiveSound("click");
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              className="md:hidden w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"
                  } text-base sm:text-lg`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Nav Menu Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden max-w-7xl mx-auto mt-2 glass-card rounded-2xl p-4 shadow-xl flex flex-col gap-3 font-semibold text-slate-700">
            <a
              href="#features"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-purple-50 flex items-center gap-3"
            >
              <i className="fa-solid fa-star text-amber-400"></i> Features
            </a>
            <a
              href="#demo"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-purple-50 flex items-center gap-3"
            >
              <i className="fa-solid fa-gamepad text-purple-500"></i> Mini Game Demo
            </a>
            <a
              href="#preview"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-purple-50 flex items-center gap-3"
            >
              <i className="fa-solid fa-mobile-screen-button text-teal-500"></i> App Peek
            </a>
            <a
              href="#languages"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-purple-50 flex items-center gap-3"
            >
              <i className="fa-solid fa-language text-blue-500"></i> Languages
            </a>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative z-10 pt-4 sm:pt-6 pb-12 sm:pb-16 px-4 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Column: Hero Text */}
          <div className="lg:col-span-6 text-center lg:text-left space-y-4 sm:space-y-6">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 border border-pink-200 text-pink-700 text-xs sm:text-sm font-bold shadow-sm animate-bounce-soft max-w-full">
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-ping flex-shrink-0"></span>
              <i className="fa-solid fa-wand-magic-sparkles text-amber-500 flex-shrink-0"></i>
              <span className="truncate sm:whitespace-normal">Award-Winning Kids App for Ages 2-8</span>
            </div>

            <h1 className="heading-font text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
              Where{" "}
              <span className="bg-gradient-to-r from-pink-500 via-purple-600 to-orange-500 bg-clip-text text-transparent">
                Learning
              </span>
              <br />
              Feels Like Play!
            </h1>

            <p className="text-sm sm:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed font-medium">
              Help your child discover alphabets, numbers, vocabulary, and games in a 100% ad-free, joyful interactive world built for early development.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-2">
              <button
                onClick={openDownloadModal}
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 text-white font-bold text-base sm:text-lg shadow-xl shadow-purple-200 hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3 group"
              >
                <i className="fa-solid fa-cloud-arrow-down text-lg sm:text-xl group-hover:animate-bounce"></i>
                <span>Download Free</span>
              </button>

              <a
                href="#demo"
                className="w-full sm:w-auto px-6 py-3.5 sm:py-4 rounded-2xl glass-card hover:bg-white text-purple-700 font-bold text-sm sm:text-base shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs shadow-sm">
                  <i className="fa-solid fa-play ml-0.5"></i>
                </div>
                <span>Try Mini Game</span>
              </a>
            </div>

            {/* Live Stats */}
            <div className="pt-6 border-t border-purple-100/60 grid grid-cols-3 gap-2 sm:gap-4 text-center lg:text-left">
              <div className="space-y-1">
                <div className="heading-font text-xl sm:text-2xl lg:text-3xl font-bold text-purple-600">
                  {statDownloads}
                </div>
                <div className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Happy Kids
                </div>
              </div>
              <div className="space-y-1">
                <div className="heading-font text-xl sm:text-2xl lg:text-3xl font-bold text-pink-500 flex items-center justify-center lg:justify-start gap-1">
                  <span>4.9</span>
                  <i className="fa-solid fa-star text-amber-400 text-sm sm:text-lg"></i>
                </div>
                <div className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  App Rating
                </div>
              </div>
              <div className="space-y-1">
                <div className="heading-font text-xl sm:text-2xl lg:text-3xl font-bold text-teal-600">
                  100+
                </div>
                <div className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Activities
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Phone Simulator */}
          <div className="lg:col-span-6 flex justify-center relative mt-4 lg:mt-0">
            <div className="absolute w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-tr from-pink-400/30 to-purple-500/30 rounded-full blur-3xl pointer-events-none -z-10 animate-pulse"></div>

            {/* Floating Mascot Badges */}
            <div className="absolute -top-6 -left-2 sm:left-4 z-20 glass-card p-3 rounded-2xl shadow-xl animate-float hidden sm:flex items-center gap-3 border-2 border-pink-200">
              <div className="w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center text-white text-lg font-bold">
                🍎
              </div>
              <div>
                <div className="heading-font text-sm font-bold text-slate-800">
                  A for Apple
                </div>
                <div className="text-[11px] text-slate-500">Phonics & Sound</div>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-2 sm:right-4 z-20 glass-card p-3 rounded-2xl shadow-xl animate-float-delayed hidden sm:flex items-center gap-3 border-2 border-purple-200">
              <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center text-white text-lg font-bold">
                🎮
              </div>
              <div>
                <div className="heading-font text-sm font-bold text-slate-800">
                  100% Safe
                </div>
                <div className="text-[11px] text-slate-500">Ad-Free Learning</div>
              </div>
            </div>

            {/* Phone Container */}
            <div className="w-full max-w-[280px] min-[380px]:max-w-[320px] h-[530px] min-[380px]:h-[580px] bg-slate-900 rounded-[40px] sm:rounded-[48px] p-3 sm:p-4 shadow-2xl border-4 border-slate-700 relative flex flex-col justify-between overflow-hidden">
              <div className="w-32 h-4 bg-slate-800 rounded-full mx-auto absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center">
                <div className="w-10 h-1 bg-slate-600 rounded-full"></div>
              </div>

              {/* Screen Area */}
              <div className="w-full h-full bg-gradient-to-b from-indigo-600 via-purple-600 to-pink-500 rounded-[36px] pt-8 px-4 pb-4 text-white flex flex-col justify-between relative overflow-hidden">
                <div className="flex items-center justify-between z-10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/40 bg-white p-0.5 shadow-sm">
                      <img
                        src="/image/logo.jpg"
                        alt="KidsGuru Logo"
                        className="w-full h-full object-cover rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/image/logo.webp";
                        }}
                      />
                    </div>
                    <span className="heading-font text-sm font-bold tracking-wide">
                      KidsGuru Live
                    </span>
                  </div>
                  <span className="text-[10px] bg-emerald-400 text-slate-900 font-extrabold px-2 py-0.5 rounded-full uppercase">
                    Kid-Safe
                  </span>
                </div>

                {/* Simulator Display Card */}
                <div className="my-auto py-2 z-10 flex flex-col items-center text-center">
                  <div className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 shadow-lg flex flex-col items-center justify-center transition-all duration-300 min-h-[220px]">
                    <div className="text-6xl mb-3 animate-bounce-soft">
                      {currentSim.icon}
                    </div>
                    <div className="heading-font text-2xl font-bold">
                      {currentSim.title}
                    </div>
                    <div className="text-xs text-purple-100 mt-1">
                      {currentSim.desc}
                    </div>
                  </div>

                  {/* Mode Buttons */}
                  <div className="grid grid-cols-3 gap-2 w-full mt-4">
                    <button
                      onClick={() => handleSimModeChange("abc")}
                      className={`py-2 px-1 rounded-xl text-xs font-bold transition-all ${simMode === "abc"
                          ? "bg-white text-purple-700 shadow-md scale-105"
                          : "bg-white/20 hover:bg-white/30 text-white"
                        }`}
                    >
                      🔤 ABCs
                    </button>
                    <button
                      onClick={() => handleSimModeChange("123")}
                      className={`py-2 px-1 rounded-xl text-xs font-bold transition-all ${simMode === "123"
                          ? "bg-white text-purple-700 shadow-md scale-105"
                          : "bg-white/20 hover:bg-white/30 text-white"
                        }`}
                    >
                      🔢 123s
                    </button>
                    <button
                      onClick={() => handleSimModeChange("words")}
                      className={`py-2 px-1 rounded-xl text-xs font-bold transition-all ${simMode === "words"
                          ? "bg-white text-purple-700 shadow-md scale-105"
                          : "bg-white/20 hover:bg-white/30 text-white"
                        }`}
                    >
                      ✏️ Words
                    </button>
                  </div>
                </div>

                {/* Bottom Screen Card */}
                <div className="bg-white/15 backdrop-blur-md rounded-2xl p-3 z-10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center text-sm font-extrabold">
                      <i className="fa-solid fa-trophy"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-xs font-bold">Daily Streak</div>
                      <div className="text-[10px] text-purple-200">
                        5 Stars Earned Today!
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => playInteractiveSound("star")}
                    className="w-7 h-7 rounded-full bg-amber-300 text-slate-900 flex items-center justify-center font-bold text-xs shadow"
                  >
                    ★
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section id="features" className="py-16 px-4 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-extrabold tracking-widest text-sky-600 uppercase bg-sky-100/90 border border-sky-300/60 px-3.5 py-1.5 rounded-full inline-block shadow-sm">
            CORE APP MODULES
          </span>
          <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-3 tracking-tight">
            Everything Your Child Needs in One App
          </h2>
          <p className="text-slate-600 text-sm sm:text-base mt-2 font-medium">
            Designed with joyful animations, structured phonics, and safe ad-free environment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
          {/* Card 1 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-sky-200/80 hover:border-sky-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-sky-500 text-white flex items-center justify-center text-xl shadow-md shadow-sky-200">
                  <i className="fa-solid fa-book-open"></i>
                </div>
                <span className="bg-sky-100 text-sky-700 font-bold text-[11px] px-3 py-1 rounded-full">
                  Phonics Fast
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Learn ABC & Phonics
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Interactive phonics, letter sounds, and vibrant illustrations designed for quick retention.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-sky-600 group-hover:text-sky-700">
              <span>Phonics & Audio Cues</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-amber-200/80 hover:border-amber-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center text-xl shadow-md shadow-amber-200">
                  <i className="fa-solid fa-pencil"></i>
                </div>
                <span className="bg-amber-100 text-amber-800 font-bold text-[11px] px-3 py-1 rounded-full">
                  Step-by-Step
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Easy Letter Tracing
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Guided stroke lines to teach fine motor skills for writing English, Hindi, and Gujarati letters.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-amber-600 group-hover:text-amber-700">
              <span>Handwriting Practice</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-purple-200/80 hover:border-purple-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-purple-600 text-white flex items-center justify-center text-xl shadow-md shadow-purple-200">
                  <i className="fa-solid fa-chart-column"></i>
                </div>
                <span className="bg-purple-100 text-purple-700 font-bold text-[11px] px-3 py-1 rounded-full">
                  Parent Portal
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Track Every Child's Growth
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Detailed reports on study time, accuracy %, and module completion for complete peace of mind.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-purple-600 group-hover:text-purple-700">
              <span>Analytics Dashboard</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>

          {/* Card 4 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-emerald-200/80 hover:border-emerald-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-sm font-extrabold shadow-md shadow-emerald-200 leading-tight text-center">
                  12<br/>34
                </div>
                <span className="bg-emerald-100 text-emerald-800 font-bold text-[11px] px-3 py-1 rounded-full">
                  Math Basics
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Learn Numbers 1–100
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Counting games, animal counters, and number tracing with audio pronunciations.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-emerald-600 group-hover:text-emerald-700">
              <span>Counting & Logic</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>

          {/* Card 5 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-rose-200/80 hover:border-rose-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-rose-500 text-white flex items-center justify-center text-xl shadow-md shadow-rose-200">
                  🎈
                </div>
                <span className="bg-rose-100 text-rose-700 font-bold text-[11px] px-3 py-1 rounded-full">
                  Interactive
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Pop & Learn Balloon Game
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Tap balloons matching target letters and numbers to boost reflexes and recognition.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-rose-600 group-hover:text-rose-700">
              <span>Balloon Pop Game</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>

          {/* Card 6 */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300 border border-cyan-200/80 hover:border-cyan-400 flex flex-col justify-between group hover:-translate-y-1">
            <div>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500 text-white flex items-center justify-center text-xl shadow-md shadow-cyan-200">
                  <i className="fa-solid fa-gamepad"></i>
                </div>
                <span className="bg-cyan-100 text-cyan-800 font-bold text-[11px] px-3 py-1 rounded-full">
                  20+ Mini Games
                </span>
              </div>
              <h3 className="heading-font text-xl font-bold text-slate-900 mt-5">
                Words, Puzzles & Memory
              </h3>
              <p className="text-slate-500 text-xs sm:text-sm mt-2 leading-relaxed">
                Picture puzzles, word completion, memory matching, and math explorer challenges.
              </p>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-cyan-600 group-hover:text-cyan-700">
              <span>Brain Development</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
            </div>
          </div>
        </div>
      </section>

      {/* DEMO MINI GAME SECTION */}
      <section id="demo" className="py-16 px-4 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="glass-dark rounded-[40px] p-6 sm:p-10 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-4 right-8 text-3xl opacity-20 animate-spin-slow">⭐</div>
          <div className="absolute bottom-6 left-6 text-4xl opacity-20 animate-bounce">🎈</div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Intro Text */}
            <div className="lg:col-span-5 space-y-4 text-center lg:text-left">
              <span className="px-3 py-1 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/40 text-xs font-bold uppercase tracking-wider">
                Interactive Demo
              </span>
              <h2 className="heading-font text-3xl sm:text-4xl font-bold leading-tight">
                Try It Yourself!<br />
                <span className="text-amber-300">Pop the Alphabet Bubbles</span>
              </h2>
              <p className="text-purple-200 text-sm sm:text-base">
                Tap or click floating bubbles in the game box to collect letters and earn star points! This gives you a preview of how interactive learning feels in KidsGuru.
              </p>

              <div className="flex items-center justify-center lg:justify-start gap-6 pt-2">
                <div className="bg-white/10 rounded-2xl px-4 py-2 border border-white/10">
                  <div className="text-xs text-purple-200 font-semibold">Your Score</div>
                  <div className="heading-font text-2xl font-bold text-amber-300">
                    {gameScore} ⭐
                  </div>
                </div>
                <button
                  onClick={resetMiniGame}
                  className="px-4 py-2 rounded-xl bg-purple-500/30 hover:bg-purple-500/50 text-white font-bold text-xs border border-purple-400/30 transition-all"
                >
                  <i className="fa-solid fa-rotate-right mr-1"></i> Restart Game
                </button>
              </div>
            </div>

            {/* Game Canvas Area */}
            <div className="lg:col-span-7 flex justify-center">
              <div
                ref={gameAreaRef}
                className="w-full h-80 sm:h-96 bg-gradient-to-b from-indigo-900/80 to-purple-900/80 rounded-3xl border-2 border-purple-400/30 relative overflow-hidden flex items-center justify-center shadow-inner cursor-pointer"
              >
                <div className="absolute z-0 text-purple-300/40 font-bold heading-font text-lg pointer-events-none text-center px-4">
                  Click or Tap Any Floating Bubble!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APP SHOWCASE SECTION */}
      <section id="preview" className="py-16 px-4 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-extrabold tracking-widest text-pink-600 uppercase bg-pink-100 px-3 py-1 rounded-full">
            App Showcase
          </span>
          <h2 className="heading-font text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            Peek Inside the Fun!
          </h2>
          <p className="text-slate-600 text-sm sm:text-base mt-2">
            Vibrant, kid-friendly interfaces designed for independent exploration.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1 */}
          <div className="glass-card rounded-3xl p-4 border border-purple-200/60 shadow-lg hover:shadow-xl transition-all group flex flex-col justify-between">
            <div className="bg-gradient-to-b from-pink-500 to-purple-600 rounded-2xl text-white text-center h-80 flex flex-col justify-between relative overflow-hidden p-2">
              <div className="flex items-center justify-between text-xs p-2 z-10 bg-black/20 backdrop-blur-md rounded-xl">
                <span className="font-bold">Gujarati & Hindi</span>
                <span className="bg-white/20 px-2 py-0.5 rounded-full">Phonics</span>
              </div>

              <div className="my-auto relative rounded-xl overflow-hidden shadow-inner h-52 group-hover:scale-105 transition-transform duration-300 bg-purple-950/40">
                <img
                  src="/image/gujarati_hindi.jpg"
                  alt="KidsGuru Gujarati Phonics"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/image/3d996b3f8c2d8380bd44c28923467f8b.webp";
                  }}
                />
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 text-xs font-semibold flex justify-around z-10">
                <span>🅰️ English</span>
                <span>અ Gujarati</span>
                <span>अ Hindi</span>
              </div>
            </div>
            <div className="p-3 text-center">
              <h4 className="heading-font font-bold text-slate-800">
                Multilingual Alphabets
              </h4>
              <p className="text-xs text-slate-500">
                Gujarati, Hindi & English sound support
              </p>
            </div>
          </div>

          {/* Card 2 */}
          <div className="glass-card rounded-3xl p-4 border border-teal-200/60 shadow-lg hover:shadow-xl transition-all group flex flex-col justify-between">
            <div className="bg-gradient-to-b from-teal-500 to-emerald-600 rounded-2xl text-white text-center h-80 flex flex-col justify-between relative overflow-hidden p-2">
              <div className="flex items-center justify-between text-xs p-2 z-10 bg-black/20 backdrop-blur-md rounded-xl">
                <span className="font-bold">Counting 1-20</span>
                <span className="bg-white/20 px-2 py-0.5 rounded-full">Math</span>
              </div>

              <div className="my-auto relative rounded-xl overflow-hidden shadow-inner h-52 group-hover:scale-105 transition-transform duration-300 bg-teal-950/40">
                <img
                  src="/image/counting.jpg"
                  alt="KidsGuru Number Counting"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/image/8d6de193420aa00dd73e6b45e5c15a13.webp";
                  }}
                />
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 text-xs font-semibold z-10">
                Interactive Animal & Object Counters
              </div>
            </div>
            <div className="p-3 text-center">
              <h4 className="heading-font font-bold text-slate-800">
                Counting & Numbers
              </h4>
              <p className="text-xs text-slate-500">
                Visual counting with audio feedback
              </p>
            </div>
          </div>

          {/* Card 3 */}
          <div className="glass-card rounded-3xl p-4 border border-amber-200/60 shadow-lg hover:shadow-xl transition-all group flex flex-col justify-between">
            <div className="bg-gradient-to-b from-amber-500 to-orange-600 rounded-2xl text-white text-center h-80 flex flex-col justify-between relative overflow-hidden p-2">
              <div className="flex items-center justify-between text-xs p-2 z-10 bg-black/20 backdrop-blur-md rounded-xl">
                <span className="font-bold">Spelling Quiz</span>
                <span className="bg-white/20 px-2 py-0.5 rounded-full">Words</span>
              </div>

              <div className="my-auto relative rounded-xl overflow-hidden shadow-inner h-52 group-hover:scale-105 transition-transform duration-300 bg-amber-950/40">
                <img
                  src="/image/spelling_quiz.jpg"
                  alt="KidsGuru Word Completion"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/image/a196c0f686c068dc3edda8085f5f5ab1.webp";
                  }}
                />
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 text-xs font-semibold z-10">
                Drag & Drop Spelling Fun
              </div>
            </div>
            <div className="p-3 text-center">
              <h4 className="heading-font font-bold text-slate-800">
                Word Completion
              </h4>
              <p className="text-xs text-slate-500">
                Build spelling confidence early
              </p>
            </div>
          </div>

          {/* Card 4 */}
          <div className="glass-card rounded-3xl p-4 border border-blue-200/60 shadow-lg hover:shadow-xl transition-all group flex flex-col justify-between">
            <div className="bg-gradient-to-b from-blue-500 to-indigo-600 rounded-2xl text-white text-center h-80 flex flex-col justify-between relative overflow-hidden p-2">
              <div className="flex items-center justify-between text-xs p-2 z-10 bg-black/20 backdrop-blur-md rounded-xl">
                <span className="font-bold">Puzzles & Colors</span>
                <span className="bg-white/20 px-2 py-0.5 rounded-full">Games</span>
              </div>

              <div className="my-auto relative rounded-xl overflow-hidden shadow-inner h-52 group-hover:scale-105 transition-transform duration-300 bg-indigo-950/40">
                <img
                  src="/image/puzzles.jpg"
                  alt="KidsGuru Educational Mini Games"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/image/a1c4cb21249edd832456483f1c56c224.webp";
                  }}
                />
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 text-xs font-semibold z-10">
                30+ Brain Development Games
              </div>
            </div>
            <div className="p-3 text-center">
              <h4 className="heading-font font-bold text-slate-800">
                Creative Games
              </h4>
              <p className="text-xs text-slate-500">
                Colors, shapes, & memory puzzles
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MULTI-LANGUAGE LEARNING SECTION */}
      <section id="languages" className="py-12 sm:py-16 px-4 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="glass-card rounded-3xl sm:rounded-[36px] p-5 sm:p-8 lg:p-12 border border-purple-100 shadow-xl mb-12 sm:mb-16 text-center">
          <span className="text-xs font-extrabold tracking-widest text-indigo-600 uppercase bg-indigo-100 px-3 py-1 rounded-full">
            Multi-Language Learning
          </span>
          <h2 className="heading-font text-2xl sm:text-4xl font-bold text-slate-900 mt-3">
            Available in <span className="text-purple-600">3 Languages</span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-base max-w-xl mx-auto mt-2">
            Help kids connect with their native language or learn a second language seamlessly.
          </p>

          <div className="grid grid-cols-1 xs:grid-cols-3 sm:flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
            <button
              onClick={() => handleLangChange("english")}
              className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 font-bold flex items-center justify-center gap-3 transition-all ${selectedLang === "english"
                  ? "border-purple-600 bg-purple-600 text-white shadow-md scale-105"
                  : "border-slate-200 hover:border-purple-400 bg-white text-slate-800"
                }`}
            >
              <span className="text-xl sm:text-2xl">Aa</span>
              <div className="text-left">
                <div className="text-xs sm:text-sm">English</div>
                <div className="text-[10px] opacity-80">Default</div>
              </div>
            </button>

            <button
              onClick={() => handleLangChange("hindi")}
              className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 font-bold flex items-center justify-center gap-3 transition-all ${selectedLang === "hindi"
                  ? "border-purple-600 bg-purple-600 text-white shadow-md scale-105"
                  : "border-slate-200 hover:border-purple-400 bg-white text-slate-800"
                }`}
            >
              <span className="text-xl sm:text-2xl text-orange-500">अ</span>
              <div className="text-left">
                <div className="text-xs sm:text-sm">Hindi</div>
                <div className="text-[10px] opacity-80">हिंदी</div>
              </div>
            </button>

            <button
              onClick={() => handleLangChange("gujarati")}
              className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-2xl border-2 font-bold flex items-center justify-center gap-3 transition-all ${selectedLang === "gujarati"
                  ? "border-purple-600 bg-purple-600 text-white shadow-md scale-105"
                  : "border-slate-200 hover:border-purple-400 bg-white text-slate-800"
                }`}
            >
              <span className="text-xl sm:text-2xl text-teal-500">અ</span>
              <div className="text-left">
                <div className="text-xs sm:text-sm">Gujarati</div>
                <div className="text-[10px] opacity-80">ગુજરાતી</div>
              </div>
            </button>
          </div>

          <div className="mt-6 sm:mt-8 bg-purple-50/70 rounded-2xl p-4 sm:p-6 max-w-md mx-auto border border-purple-100 flex items-center justify-between">
            <div className="text-left">
              <div className="text-[10px] sm:text-xs text-purple-600 font-bold uppercase tracking-wider">
                Live Vocabulary Card
              </div>
              <div className="heading-font text-lg sm:text-2xl font-bold text-slate-800 mt-1">
                {currentLang.word}
              </div>
            </div>
            <div className="text-3xl sm:text-4xl">{currentLang.icon}</div>
          </div>
        </div>

        {/* WHY PARENTS LOVE KIDSGURU */}
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
          <h2 className="heading-font text-2xl sm:text-4xl font-bold text-slate-900">
            Why Parents Love KidsGuru
          </h2>
          <p className="text-slate-600 text-xs sm:text-base mt-2">
            Trusted by thousands of families for safe, screen-time friendly education.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-emerald-100 space-y-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center text-lg sm:text-xl font-bold">
              <i className="fa-solid fa-shield-halved"></i>
            </div>
            <h4 className="heading-font font-bold text-slate-800 text-base sm:text-lg">
              100% Safe & Ad-Free
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              No external ads, no popups, and no accidental in-app purchases. Completely safe environment.
            </p>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-purple-100 space-y-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center text-lg sm:text-xl font-bold">
              <i className="fa-solid fa-face-smile-beam"></i>
            </div>
            <h4 className="heading-font font-bold text-slate-800 text-base sm:text-lg">
              Builds Confidence
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Positive audio cues and star badges reward effort and encourage independent learning habits.
            </p>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-amber-100 space-y-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center text-lg sm:text-xl font-bold">
              <i className="fa-solid fa-chart-line"></i>
            </div>
            <h4 className="heading-font font-bold text-slate-800 text-base sm:text-lg">
              Track Progress
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Parents can view simple weekly reports showing activity completion and time spent.
            </p>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-pink-100 space-y-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center text-lg sm:text-xl font-bold">
              <i className="fa-solid fa-user-graduate"></i>
            </div>
            <h4 className="heading-font font-bold text-slate-800 text-base sm:text-lg">
              Expert Designed
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Curriculum aligned with early childhood preschool standards for age group 2 to 8 years.
            </p>
          </div>
        </div>

        {/* Testimonials Title Header */}
        <div className="mt-12 sm:mt-16 text-center max-w-2xl mx-auto mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-extrabold tracking-wider text-emerald-700 uppercase bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 rounded-full shadow-sm">
            <i className="fa-brands fa-google-play text-emerald-500 text-sm"></i>
            <span>Real Google Play Store Reviews</span>
          </div>
          <h3 className="heading-font text-2xl sm:text-3xl font-bold text-slate-900 mt-3">
            Real Feedback from Google Play Store Parents
          </h3>
          <p className="text-slate-600 text-xs sm:text-sm mt-1.5">
            Verified ratings and reviews from families who use KidsGuru everyday.
          </p>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-slate-200/60 shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex text-amber-400 gap-1 text-xs sm:text-sm">★★★★★</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200/60 px-2 py-0.5 rounded-full">
                  <i className="fa-brands fa-google-play text-emerald-500 text-[10px]"></i> Google Play
                </span>
              </div>
              <p className="text-slate-700 text-xs sm:text-sm italic">
                "I’ve been using KidsGuru for a few weeks, and the learning experience has been wonderful. The alphabet, phonics, numbers, and tracing activities are engaging, and it’s easy to see steady progress."
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-pink-400 text-white font-bold flex items-center justify-center text-xs">
                K
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Kunal</div>
                <div className="text-[10px] text-slate-400">Verified Parent Review</div>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-slate-200/60 shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex text-amber-400 gap-1 text-xs sm:text-sm">★★★★★</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200/60 px-2 py-0.5 rounded-full">
                  <i className="fa-brands fa-google-play text-emerald-500 text-[10px]"></i> Google Play
                </span>
              </div>
              <p className="text-slate-700 text-xs sm:text-sm italic">
                "As a parent, I appreciate how easy this app is to use. The lessons are well organized, and my child enjoys practicing alphabets, numbers, and phonics. Definitely a useful app for preschool learning."
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white font-bold flex items-center justify-center text-xs">
                S
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Shivaay</div>
                <div className="text-[10px] text-slate-400">Verified Parent Review</div>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5 sm:p-6 border border-slate-200/60 shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex text-amber-400 gap-1 text-xs sm:text-sm">★★★★★</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200/60 px-2 py-0.5 rounded-full">
                  <i className="fa-brands fa-google-play text-emerald-500 text-[10px]"></i> Google Play
                </span>
              </div>
              <p className="text-slate-700 text-xs sm:text-sm italic">
                "KidsGuru is an amazing ABC and 123 preschool learning app. My child loves learning the alphabet, numbers, phonics, counting, colors, and shapes through fun games and interactive activities"
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500 text-white font-bold flex items-center justify-center text-xs">
                A
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Aryan</div>
                <div className="text-[10px] text-slate-400">Verified Parent Review</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROCKET CTA SECTION */}
      <section className="py-12 sm:py-16 px-4 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="rounded-3xl sm:rounded-[40px] bg-gradient-to-r from-pink-500 via-purple-600 to-orange-500 p-6 sm:p-14 text-white text-center shadow-2xl relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-black/10 rounded-full blur-xl pointer-events-none"></div>

          <div className="relative z-10 max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-white/20 backdrop-blur-md mx-auto flex items-center justify-center text-2xl sm:text-3xl animate-bounce">
              🚀
            </div>

            <h2 className="heading-font text-2xl sm:text-5xl font-bold leading-tight">
              Start Your Child's Learning Journey Today!
            </h2>

            <p className="text-purple-100 text-xs sm:text-base leading-relaxed">
              Join over 500,000 happy families and give your child the gift of joyful, ad-free learning.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-2 sm:pt-4">
              <button
                onClick={handleIosClick}
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-white text-slate-900 hover:bg-slate-100 font-bold text-sm sm:text-base shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3"
              >
                <i className="fa-brands fa-apple text-xl sm:text-2xl"></i>
                <div className="text-left leading-tight">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] sm:text-[10px] text-slate-500 uppercase">Download for</span>
                    {/* <span className="bg-amber-100 text-amber-800 text-[8px] font-extrabold px-1.5 py-0.5 rounded-full uppercase">Coming Soon</span> */}
                  </div>
                  <div className="text-xs sm:text-sm font-extrabold">iOS App Store</div>
                </div>
              </button>

              <button
                onClick={openDownloadModal}
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 font-bold text-sm sm:text-base shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3 border border-white/20"
              >
                <i className="fa-brands fa-google-play text-xl sm:text-2xl text-emerald-400"></i>
                <div className="text-left leading-tight">
                  <div className="text-[9px] sm:text-[10px] text-slate-400 uppercase">Download for</div>
                  <div className="text-xs sm:text-sm font-extrabold">Google Play</div>
                </div>
              </button>
            </div>

            <div className="text-[11px] sm:text-xs text-purple-200 flex flex-wrap justify-center gap-3 sm:gap-6 pt-2 sm:pt-4 font-medium">
              <span>
                <i className="fa-solid fa-circle-check text-amber-300 mr-1"></i> Free 7-Day Trial
              </span>
              <span>
                <i className="fa-solid fa-circle-check text-amber-300 mr-1"></i> No Credit Card Needed
              </span>
              <span>
                <i className="fa-solid fa-circle-check text-amber-300 mr-1"></i> Cancel Anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-950 text-slate-300 pt-14 pb-8 px-4 lg:px-12 relative z-10 border-t border-purple-500/20">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Multi-Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 pb-12 border-b border-slate-800/80 items-stretch">
            
            {/* Brand Card Column (Span 5) */}
            <div className="md:col-span-5 bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-7 backdrop-blur-md flex flex-col justify-between space-y-5 shadow-xl relative overflow-hidden group">
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl pointer-events-none group-hover:bg-pink-500/20 transition-all"></div>
              
              <div className="space-y-3.5">
                <a href="#top" className="flex items-center gap-3.5 group/logo inline-flex">
                  <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg shadow-purple-500/20 border-2 border-purple-400/50 bg-white flex items-center justify-center p-0.5 group-hover/logo:scale-105 transition-transform duration-300">
                    <img
                      src="/image/logo.jpg"
                      alt="KidsGuru Logo"
                      className="w-full h-full object-cover rounded-xl"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/image/logo.webp";
                      }}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="heading-font text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-300 to-amber-300 bg-clip-text text-transparent">
                      KidsGuru
                    </span>
                    <span className="text-[10px] font-semibold tracking-wider text-purple-400 uppercase">
                      Interactive Learning
                    </span>
                  </div>
                </a>

                <p className="text-xs leading-relaxed text-slate-300/90 font-medium">
                  Turn everyday screen time into early learning success with ad-free games, phonics, numbers, and multilingual vocabulary built for young minds.
                </p>

                {/* Rating Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/20 text-purple-300 text-xs font-semibold">
                  <span className="text-amber-400">★★★★★</span>
                  <span>4.9 App Rating</span>
                  <span className="text-purple-400">&bull;</span>
                  <span className="text-slate-400">500K+ Kids</span>
                </div>
              </div>

              {/* Social Pill Buttons */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Connect With Us</span>
                <div className="flex items-center gap-2">
                  <a
                    href="#top"
                    className="w-9 h-9 rounded-xl bg-white/5 hover:bg-pink-500/20 text-slate-300 hover:text-pink-400 border border-white/10 flex items-center justify-center transition-all hover:scale-110"
                    aria-label="Facebook"
                  >
                    <i className="fa-brands fa-facebook-f text-xs"></i>
                  </a>
                  <a
                    href="#top"
                    className="w-9 h-9 rounded-xl bg-white/5 hover:bg-purple-500/20 text-slate-300 hover:text-purple-400 border border-white/10 flex items-center justify-center transition-all hover:scale-110"
                    aria-label="Instagram"
                  >
                    <i className="fa-brands fa-instagram text-xs"></i>
                  </a>
                  <a
                    href="#top"
                    className="w-9 h-9 rounded-xl bg-white/5 hover:bg-rose-500/20 text-slate-300 hover:text-rose-400 border border-white/10 flex items-center justify-center transition-all hover:scale-110"
                    aria-label="YouTube"
                  >
                    <i className="fa-brands fa-youtube text-xs"></i>
                  </a>
                  <a
                    href="#top"
                    className="w-9 h-9 rounded-xl bg-white/5 hover:bg-blue-500/20 text-slate-300 hover:text-blue-400 border border-white/10 flex items-center justify-center transition-all hover:scale-110"
                    aria-label="Twitter"
                  >
                    <i className="fa-brands fa-twitter text-xs"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Navigation Column (Span 4) */}
            <div className="md:col-span-4 space-y-4 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <i className="fa-solid fa-compass text-pink-400 text-xs"></i> Explore Features
                </div>

                <div className="grid grid-cols-1 gap-2.5 text-xs">
                  <a
                    href="#features"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-pink-500/10 border border-white/5 hover:border-pink-500/30 text-slate-300 hover:text-pink-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-pink-500/20 text-pink-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-star"></i>
                      </div>
                      <span className="font-semibold">Core Features</span>
                    </div>
                    <i className="fa-solid fa-arrow-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-pink-400 transition-all"></i>
                  </a>

                  <a
                    href="#demo"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-purple-500/10 border border-white/5 hover:border-purple-500/30 text-slate-300 hover:text-purple-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-gamepad"></i>
                      </div>
                      <span className="font-semibold">Mini Game Demo</span>
                    </div>
                    <i className="fa-solid fa-arrow-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-purple-400 transition-all"></i>
                  </a>

                  <a
                    href="#preview"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-teal-500/10 border border-white/5 hover:border-teal-500/30 text-slate-300 hover:text-teal-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-teal-500/20 text-teal-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-mobile-screen-button"></i>
                      </div>
                      <span className="font-semibold">App Showcase</span>
                    </div>
                    <i className="fa-solid fa-arrow-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-teal-400 transition-all"></i>
                  </a>

                  <a
                    href="#languages"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-blue-500/10 border border-white/5 hover:border-blue-500/30 text-slate-300 hover:text-blue-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-language"></i>
                      </div>
                      <span className="font-semibold">Multi-Language</span>
                    </div>
                    <i className="fa-solid fa-arrow-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-blue-400 transition-all"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Safety & Help Column (Span 3) */}
            <div className="md:col-span-3 space-y-4 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/30 text-teal-300 text-xs font-bold uppercase tracking-wider mb-4">
                  <i className="fa-solid fa-shield-halved text-teal-400 text-xs"></i> Safety & Help
                </div>

                <div className="grid grid-cols-1 gap-2.5 text-xs">
                  <a
                    href="#top"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-emerald-500/10 border border-white/5 hover:border-emerald-500/30 text-slate-300 hover:text-emerald-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-book"></i>
                      </div>
                      <span className="font-semibold">Parent Guide</span>
                    </div>
                    <i className="fa-solid fa-chevron-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-emerald-400 transition-all"></i>
                  </a>

                  <a
                    href="#top"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-indigo-500/10 border border-white/5 hover:border-indigo-500/30 text-slate-300 hover:text-indigo-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-lock"></i>
                      </div>
                      <span className="font-semibold">Privacy Policy</span>
                    </div>
                    <i className="fa-solid fa-chevron-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-indigo-400 transition-all"></i>
                  </a>

                  <a
                    href="#top"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-amber-500/10 border border-white/5 hover:border-amber-500/30 text-slate-300 hover:text-amber-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-file-contract"></i>
                      </div>
                      <span className="font-semibold">Terms of Service</span>
                    </div>
                    <i className="fa-solid fa-chevron-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-amber-400 transition-all"></i>
                  </a>

                  <a
                    href="#top"
                    className="p-3 rounded-2xl bg-white/5 hover:bg-rose-500/10 border border-white/5 hover:border-rose-500/30 text-slate-300 hover:text-rose-300 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-rose-500/20 text-rose-400 flex items-center justify-center text-xs">
                        <i className="fa-solid fa-headset"></i>
                      </div>
                      <span className="font-semibold">Contact Support</span>
                    </div>
                    <i className="fa-solid fa-chevron-right text-[10px] text-slate-500 group-hover:translate-x-1 group-hover:text-rose-400 transition-all"></i>
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Copyright Bar */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping"></span>
              <span className="font-medium text-slate-300">Loved by 500,000+ Families Worldwide</span>
            </div>
            <div>&copy; {new Date().getFullYear()} KidsGuru. All rights reserved.</div>
          </div>
        </div>
      </footer>

      {/* DOWNLOAD MODAL POPUP */}
      {downloadModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-card bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto text-center relative shadow-2xl border-2 border-purple-200 animate-bounce-soft">
            <button
              onClick={closeDownloadModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold hover:bg-slate-200"
            >
              ✕
            </button>

            <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-md border-2 border-purple-300 mx-auto mb-4 bg-white p-1">
              <img
                src="/image/logo.jpg"
                alt="KidsGuru Logo"
                className="w-full h-full object-cover rounded-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/image/logo.webp";
                }}
              />
            </div>

            <h3 className="heading-font text-2xl font-bold text-slate-900">
              Download KidsGuru
            </h3>
            <p className="text-xs text-slate-600 mt-2">
              Choose your platform to start your child's 7-day free trial.
            </p>

            <div className="space-y-3 mt-6">
              <button
                onClick={handleIosClick}
                className="w-full p-4 rounded-2xl bg-slate-900 text-white font-bold flex items-center justify-between hover:bg-slate-800 transition-all border border-slate-700"
              >
                <div className="flex items-center gap-3">
                  <i className="fa-brands fa-apple text-xl"></i>
                  <span>Download on iOS App Store</span>
                </div>
                <span className="bg-amber-400 text-slate-950 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase">
                  Coming Soon
                </span>
              </button>
              <a
                href={PLAY_STORE_URL}
                onClick={(e) => {
                  triggerConfetti();
                }}
                className="p-4 rounded-2xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-3 hover:bg-emerald-500 transition-all"
              >
                <i className="fa-brands fa-google-play text-xl"></i>
                <span>Get it on Google Play</span>
              </a>
            </div>

            <div className="mt-4 text-[11px] text-slate-400">
              Safe download &bull; Rated 4.9★ by 500,000+ parents
            </div>
          </div>
        </div>
      )}

      {/* iOS COMING SOON MODAL */}
      {iosModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-card bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full text-center relative shadow-2xl border-2 border-purple-200 animate-bounce-soft">
            <button
              onClick={() => {
                playInteractiveSound("click");
                setIosModalOpen(false);
              }}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold hover:bg-slate-200"
            >
              ✕
            </button>

            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-pink-500 to-purple-600 text-white mx-auto mb-4 flex items-center justify-center text-3xl shadow-lg shadow-purple-200">
              <i className="fa-brands fa-apple"></i>
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-xs mb-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
              <span>iOS Version Coming Soon</span>
            </div>

            <h3 className="heading-font text-2xl font-bold text-slate-900 mt-2">
              Apple App Store Coming Soon! 🚀
            </h3>

            <p className="text-xs sm:text-sm text-slate-600 mt-3 leading-relaxed">
              We are putting the final touches on our iOS app for iPhone & iPad. It will be launched on the Apple App Store very soon!
            </p>

            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-3.5 mt-4 text-xs text-purple-700 font-medium flex items-center gap-2 text-left">
              <i className="fa-solid fa-circle-info text-purple-500 text-base flex-shrink-0"></i>
              <span>In the meantime, you can download KidsGuru on Google Play for Android or try our Web Demo!</span>
            </div>

            <button
              onClick={() => {
                playInteractiveSound("click");
                setIosModalOpen(false);
              }}
              className="w-full mt-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;